import { EIcon } from '@NGComponents/NGIcon/NGIcon.enum';
import useUserPersonalData from '@apis/queries/useUserPersonalData';
import { ShareProfile } from '@characterOverview/components/ShareProfile/ShareProfile';
import { ESocialShareType } from '@characterOverview/components/ShareProfile/ShareProfile.enum';
import useAppRouter from '@store/appRouter/hooks/useAppRouter';
import useLogin from '@store/userStore/hooks/useLogin';
import useLogout from '@store/userStore/hooks/useLogout';
import { APP_LINKS, DEFAULT_TRANSITION_TIMEOUT, NAVBAR_LINKS, URL_PARAMS } from '@utils/variables';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CSSTransition, SwitchTransition } from 'react-transition-group';
import useIsLoggedIn from 'src/apis/queries/useIsLoggedIn';
import Collapse from 'src/components/common/Collapse';
import Mainlinks from 'src/components/navbar/Mainlinks';
import Sublinks from 'src/components/navbar/Sublinks';
import useResponsive from 'src/hooks/contexts/useResponsive';
import useCharacterRouter from 'src/hooks/routers/useCharacterRouter';
import useCurrentPageId from 'src/store/appState/hooks/useCurrentPageId';
import useCurrentSubpage from 'src/store/appState/hooks/useCurrentSubpage';
import Flex from 'src/styles/layouts/Flex';
import { EFlex } from 'src/styles/layouts/Flex/Flex.enum';
import Spacing from 'src/styles/layouts/Spacing';
import NGButton from 'src/styles/theme/components/NGButton';
import NGHeading from 'src/styles/theme/components/NGHeading';
import NGLogo from 'src/styles/theme/components/NGLogo';
import { ETextType } from 'src/styles/theme/components/NGText/NGText.enum';
import { EMediaQuery, ESize, ETextColor } from 'src/styles/theme/utils/enums';
import { StyledNavbarLight } from '../Mainlinks/Mainlinks.styles';
import { ISubmenu } from '../Sublinks/Sublinks.type';
import { FlexMobileButtonEx, StyledBurgerMenu, StyledNavbar } from './Navbar.styles';

// navbar with logo and links
const Navbar: FC = () => {
	const { isSmallerThanMd, screenFrames } = useResponsive();

	const router = useRouter();
	// init app router atom
	useAppRouter();

	const [currentPageId] = useCurrentPageId();
	const [{ id: currentSubpageId }] = useCurrentSubpage();

	const { isLoggedIn } = useIsLoggedIn();
	const { logout } = useLogout();
	const { login } = useLogin();
	const { isSuperUser } = useUserPersonalData();

	const { isInCustomizationView } = useCharacterRouter();

	const [isNavbarOpen, setIsNavbarOpen] = useState(false);

	const navbarRef = useRef<any>();

	// height ov opened navbar
	const customHeight = Math.floor((screenFrames?.height || 0) - 142) + 'px';

	const mobileLocation = useMemo<string>(() => {
		const page = NAVBAR_LINKS.find(({ id }) => id === currentPageId);
		const subpage = page?.submenus?.find(({ id }) => id === currentSubpageId);
		const subpageName = subpage?.label || '';

		if (!subpageName) {
			return '';
		}

		return subpageName;
	}, [currentPageId, currentSubpageId]);

	const onClickOutside = useCallback(
		(event: MouseEvent) => {
			const { target } = event;

			if (!isNavbarOpen) {
				return;
			}

			// close navbar if click outside
			if (target instanceof Node && !navbarRef?.current?.contains(target)) {
				setIsNavbarOpen(false);
			}
		},
		[isNavbarOpen, setIsNavbarOpen]
	);

	useEffect(() => {
		if (isSmallerThanMd) {
			if (isNavbarOpen) {
				document.addEventListener('click', onClickOutside);
			} else {
				document.removeEventListener('click', onClickOutside);
			}
		}
		return () => {
			document.removeEventListener('click', onClickOutside);
		};
	}, [isNavbarOpen, onClickOutside, isSmallerThanMd]);

	useEffect(() => {
		// set the right submenu depending on url params
		if (currentSubpageId && currentPageId) {
			// get page from activeId
			const page = NAVBAR_LINKS.find(({ id }) => id === currentPageId);

			if (!page) {
				console.error('Page not found');
				return;
			}

			const submenus = page.submenus || [];

			// check if the currentSubpage is valid
			if (!(currentSubpageId && submenus.find(({ id }: ISubmenu) => id === currentSubpageId))) {
				console.error('subpage not valid');
				return;
			}

			// get type from the current submenus
			const { type, isDisabled } = submenus.find(({ id }: ISubmenu) => id === currentSubpageId) || {};

			if (isDisabled && !isSuperUser) {
				console.error("can't access this page");
				return;
			}
			if (!type) {
				console.error("can't get submenu type");
				return;
			}
			// @ts-ignore
			window[URL_PARAMS.subpage] = currentSubpageId;
		}
	}, [currentPageId, isSuperUser, currentSubpageId]);

	// disable navbar in auth page
	if (router.pathname === APP_LINKS.auth.href) return <></>;

	return (
		<StyledNavbar ref={navbarRef} isOpen={isNavbarOpen} isNavigationDisabled={isInCustomizationView}>
			<Flex
				fullWidth
				fullHeight
				horizontal={EFlex.between}
				vertical={EFlex.center}
				paddingX={ESize.xl}
				lgPaddingX={ESize.s}
				mdPaddingX={ESize.xl}
				smPaddingX={ESize.unset}
			>
				<Flex vertical={EFlex.center} horizontal={EFlex.start} height={70} mdHeight={52}>
					<NGLogo size={isSmallerThanMd ? ESize.m : ESize.l} betaVisible />

					{!isSmallerThanMd ? (
						<>
							<Mainlinks activeId={currentPageId} isHidden={isInCustomizationView} />
						</>
					) : null}
				</Flex>

				<Flex vertical={EFlex.center} horizontal={EFlex.end} height={70} mdHeight={52}>
					{isSmallerThanMd ? (
						<>
							<NGHeading
								type={ETextType.h4}
								textColor={ETextColor.gold}
								marginRight={ESize['3xs']}
								isHidden={isNavbarOpen || isInCustomizationView}
							>
								{mobileLocation}
							</NGHeading>
							<StyledBurgerMenu
								data-cy='burger-menu-button'
								isActive={isNavbarOpen}
								onClick={() => setIsNavbarOpen(!isNavbarOpen)}
								isHidden={isInCustomizationView}
							>
								<span />
								<span />
								<span />
							</StyledBurgerMenu>
						</>
					) : (
						<Sublinks
							activeId={currentPageId}
							hiddenRange={[EMediaQuery.md, undefined]}
							isHidden={isInCustomizationView}
						/>
					)}
				</Flex>
			</Flex>

			{isSmallerThanMd ? (
				<>
					<ShareProfile shareVariables={['']} isHidden={!isNavbarOpen} shareType={ESocialShareType.app} />

					<Collapse isOpen={isNavbarOpen} customHeight={customHeight}>
						<StyledNavbarLight>
							<Image
								priority={true}
								src={'/assets/character/light-avatar.webp'}
								alt={''}
								layout='fill'
								objectFit='cover'
								draggable={false}
							/>
						</StyledNavbarLight>
						<Mainlinks activeId={currentPageId} onClick={() => setIsNavbarOpen(false)} />
					</Collapse>

					<SwitchTransition>
						<CSSTransition
							key={isLoggedIn ? 'logged-in' : 'logged-out'}
							classNames='fade'
							timeout={DEFAULT_TRANSITION_TIMEOUT['300']}
						>
							{!isLoggedIn ? (
								<NGButton
									dataCy='login-button'
									isHidden={!isNavbarOpen}
									onClick={login}
									smPaddingX={ESize['2xs']}
									height={44}
									startIcon={EIcon.github}
									marginX='auto'
								>
									Log in with GitHub
								</NGButton>
							) : (
								<FlexMobileButtonEx marginTop={8} height='100%' horizontal={EFlex.center} isClosed={!isNavbarOpen}>
									<NGButton
										dataCy='logout-button'
										isHidden={!isNavbarOpen || !isLoggedIn}
										onClick={logout}
										smPaddingX={ESize['2xs']}
										height={44}
									>
										Log out
									</NGButton>

									<Spacing size={ESize['2xs']} />

									<NGButton
										data-cy='account-settings-button'
										isHidden={!isNavbarOpen || !isLoggedIn}
										disabled={!isLoggedIn}
										smPaddingX={ESize['2xs']}
										onClick={() => {
											setIsNavbarOpen(false);
											router.push(APP_LINKS.settings.href);
										}}
										height={44}
									>
										Account Settings
									</NGButton>
								</FlexMobileButtonEx>
							)}
						</CSSTransition>
					</SwitchTransition>
				</>
			) : null}
		</StyledNavbar>
	);
};

export { Navbar };
